import type { ColumnLayout } from './columnConfigState';

export interface ColumnLayoutStorage {
  read: (tableId: string) => ColumnLayout | null;
  write: (tableId: string, layout: ColumnLayout) => void;
}

const storageKey = (tableId: string): string => `column-layout:${tableId}`;

const isLayout = (value: unknown): value is ColumnLayout => {
  if (typeof value !== 'object' || value === null || !('order' in value) || !('visible' in value))
    return false;

  const validOrder =
    Array.isArray(value.order) && value.order.every((key) => typeof key === 'string');
  const validVisibility =
    typeof value.visible === 'object' &&
    value.visible !== null &&
    !Array.isArray(value.visible) &&
    Object.values(value.visible).every((visible) => typeof visible === 'boolean');

  return validOrder && validVisibility;
};

export const localColumnLayoutStorage: ColumnLayoutStorage = {
  read: (tableId) => {
    try {
      const raw = globalThis.localStorage?.getItem(storageKey(tableId));
      if (raw === null || raw === undefined) return null;
      const parsed: unknown = JSON.parse(raw);
      return isLayout(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },
  write: (tableId, layout) => {
    try {
      globalThis.localStorage?.setItem(storageKey(tableId), JSON.stringify(layout));
    } catch {
      return;
    }
  },
};

export const memoryColumnLayoutStorage = (): ColumnLayoutStorage => {
  const layouts = new Map<string, ColumnLayout>();
  return {
    read: (tableId) => layouts.get(tableId) ?? null,
    write: (tableId, layout) => {
      layouts.set(tableId, layout);
    },
  };
};
